// src/components/composition/RollingPeriodInput.tsx
//
// The one "Last N <unit>" input (plan 2026-10-16-a-dashboard-block-may-carry-
// its-own-period, 4b; ADR-0445). The header period picker, the report's
// period select, the date range control's default and a dashboard block's own
// period all build a `rolling_<n>_<unit>[_incl]` spelling; they render THIS
// component, so none of them can offer a unit, a bound or an "include
// current" the others do not.
//
// It edits parts, never a string: the caller spells them with `rollingPreset`
// and decides when the choice applies (the header on Apply, the report at
// once). Every change hands out a whole, valid window — N is clamped to the
// unit's bound here, including when a unit switch shrinks the bound (100
// weeks → 36 months), so `rollingPreset` never throws on what leaves.
//
// The help line names the window in words AND in dates, resolved against
// `now`: "include current" is the one choice whose effect is not obvious from
// its label, and the dates are what makes it so.

import { Checkbox, Group, NumberInput, Select, Stack, Text, type MantineSize } from "@mantine/core";
import { format, isSameDay } from "date-fns";
import { enGB } from "date-fns/locale";

import {
  ROLLING_MAX,
  ROLLING_UNIT_OPTIONS,
  formatPeriodDates,
  rollingIncludeCurrentLabel,
  rollingWindowDates,
  rollingWindowMeaning,
  type RollingParts,
  type RollingUnit,
} from "@/utils/comparisonWindow";

/** The label of the row that opens this input in a period Select (the report
 *  period, the date range control's default). One string, so the two
 *  selects name the row alike. */
export const ROLLING_ROW_LABEL = "Last N days to years…";

type Props = {
  value: RollingParts;
  onChange: (parts: RollingParts) => void;
  /** The day the help line resolves the window against. Defaults to the
   *  current time; tests pass a fixed one. */
  now?: Date;
  /** The days a report answers this window over (its complete days, plan
   *  2026-10-28-a-report-page-counts-complete-days), or null when it has none
   *  yet. Absent (a dashboard): the help line names the window alone. */
  completeDays?: (parts: RollingParts) => { startDate: Date; endDate: Date } | null;
  disabled?: boolean;
  /** `xs` in the header picker's narrow preset column, `sm` in a panel. */
  size?: MantineSize;
  /** A visible label above the number ("Last"). Absent: the number carries
   *  an aria-label instead. */
  numberLabel?: string;
  /** Portal the unit dropdown (the default). Inside a Popover pass `false`:
   *  a click in a portalled dropdown is a click outside the popover, which
   *  closes it. */
  withinPortal?: boolean;
  /** Keep this many help lines of height, so a longer sentence for another
   *  unit does not move what sits below the input. */
  reserveHelpLines?: number;
  /** Prefix for the parts' test ids: `-n`, `-unit`, `-incl`, `-help`. */
  "data-testid"?: string;
};

/** Clamp a typed or carried-over N into 1..ROLLING_MAX[unit]. */
function clampN(n: number, unit: RollingUnit): number {
  return Math.min(Math.max(Math.round(n) || 1, 1), ROLLING_MAX[unit]);
}

/** "Whole calendar months, ending last month: 1 Sep – 30 Nov 2026." */
export function rollingPeriodHelp(
  parts: RollingParts,
  now: Date,
  completeDays?: { startDate: Date; endDate: Date } | null,
): string {
  const window = rollingWindowDates(now, parts);
  const datesOf = ({ startDate, endDate }: { startDate: Date; endDate: Date }) =>
    isSameDay(startDate, endDate)
      ? format(startDate, "d MMM yyyy", { locale: enGB })
      : formatPeriodDates(startDate, endDate);
  const help = `${rollingWindowMeaning(parts)}: ${datesOf(window)}.`;
  // A report counts complete days: say so only where that moves the window.
  if (completeDays === undefined) return help;
  if (completeDays === null) return `${help} The report counts complete days: none yet.`;
  if (isSameDay(completeDays.startDate, window.startDate) && isSameDay(completeDays.endDate, window.endDate)) return help;
  return `${help} The report counts complete days: ${datesOf(completeDays)}.`;
}

export default function RollingPeriodInput({
  value,
  onChange,
  now,
  completeDays,
  disabled,
  size = "sm",
  numberLabel,
  withinPortal = true,
  reserveHelpLines,
  "data-testid": testId,
}: Props) {
  const emit = (n: number, unit: RollingUnit, incl: boolean) =>
    onChange({ n: clampN(n, unit), unit, incl });
  const tid = (part: string) => (testId ? `${testId}-${part}` : undefined);

  return (
    <Stack gap={6}>
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <NumberInput
          size={size}
          label={numberLabel}
          aria-label={numberLabel ? undefined : "Number of periods"}
          value={value.n}
          min={1}
          max={ROLLING_MAX[value.unit]}
          step={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={(next) => {
            const n = typeof next === "number" ? next : Number(next);
            if (Number.isFinite(n)) emit(n, value.unit, value.incl);
          }}
          disabled={disabled}
          w={size === "xs" ? 84 : 96}
          data-testid={tid("n")}
        />
        <Select
          size={size}
          aria-label="Unit"
          data={ROLLING_UNIT_OPTIONS as { value: RollingUnit; label: string }[]}
          value={value.unit}
          onChange={(unit) => {
            if (unit) emit(value.n, unit as RollingUnit, value.incl);
          }}
          allowDeselect={false}
          comboboxProps={{ withinPortal }}
          disabled={disabled}
          style={{ flex: 1, minWidth: 0 }}
          data-testid={tid("unit")}
        />
      </Group>
      <Checkbox
        size={size === "xs" ? "xs" : "sm"}
        label={rollingIncludeCurrentLabel(value.unit)}
        checked={value.incl}
        onChange={(event) => emit(value.n, value.unit, event.currentTarget.checked)}
        disabled={disabled}
        data-testid={tid("incl")}
      />
      <Text
        size="xs"
        c="dimmed"
        data-testid={tid("help")}
        style={
          reserveHelpLines
            ? {
                minHeight: `calc(${reserveHelpLines} * var(--mantine-line-height-xs) * var(--mantine-font-size-xs))`,
              }
            : undefined
        }
      >
        {rollingPeriodHelp(value, now ?? new Date(), completeDays?.(value))}
      </Text>
    </Stack>
  );
}
